import React, { useMemo } from 'react';
import { diffWords, Change } from 'diff';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface TextDiffSummaryProps {
  original: string;
  modified: string;
  showInlineDiff?: boolean;
  maxInlineLength?: number;
  className?: string;
}

interface DiffStats {
  wordsAdded: number;
  wordsRemoved: number;
  wordsUnchanged: number;
  totalChanges: number;
  percentChanged: number;
}

function computeDiffStats(changes: Change[]): DiffStats {
  let wordsAdded = 0;
  let wordsRemoved = 0;
  let wordsUnchanged = 0;

  for (const change of changes) {
    // Count words (split by whitespace)
    const words = change.value.trim().split(/\s+/).filter(w => w.length > 0).length;

    if (change.added) {
      wordsAdded += words;
    } else if (change.removed) {
      wordsRemoved += words;
    } else {
      wordsUnchanged += words;
    }
  }

  const totalWords = wordsAdded + wordsRemoved + wordsUnchanged;
  const totalChanges = wordsAdded + wordsRemoved;
  const percentChanged = totalWords > 0 ? Math.round((totalChanges / totalWords) * 100) : 0;

  return {
    wordsAdded,
    wordsRemoved,
    wordsUnchanged,
    totalChanges,
    percentChanged,
  };
}

export function TextDiffSummary({
  original,
  modified,
  showInlineDiff = true,
  maxInlineLength = 2000,
  className,
}: TextDiffSummaryProps) {
  const { changes, stats } = useMemo(() => {
    const changes = diffWords(original || '', modified || '');
    const stats = computeDiffStats(changes);
    return { changes, stats };
  }, [original, modified]);

  const shouldShowInline = showInlineDiff &&
    (original?.length || 0) + (modified?.length || 0) < maxInlineLength * 2;

  if (stats.totalChanges === 0) {
    return (
      <div className={cn("text-sm text-muted-foreground", className)}>
        No changes detected
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {/* Summary badges */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm font-medium">Changes:</span>
        {stats.wordsAdded > 0 && (
          <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800">
            +{stats.wordsAdded} word{stats.wordsAdded !== 1 ? 's' : ''} added
          </Badge>
        )}
        {stats.wordsRemoved > 0 && (
          <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800">
            -{stats.wordsRemoved} word{stats.wordsRemoved !== 1 ? 's' : ''} removed
          </Badge>
        )}
        <span className="text-xs text-muted-foreground">
          ({stats.percentChanged}% of content changed)
        </span>
      </div>

      {/* Inline word-level diff */}
      {shouldShowInline && (
        <div className="border rounded-md p-3 bg-muted/30 max-h-60 overflow-y-auto">
          <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed">
            {changes.map((part, i) => (
              <span
                key={i}
                className={cn(
                  part.added && "bg-green-200 dark:bg-green-900/50 text-green-900 dark:text-green-100 rounded px-0.5",
                  part.removed && "bg-red-200 dark:bg-red-900/50 text-red-900 dark:text-red-100 rounded px-0.5 line-through"
                )}
              >
                {part.value}
              </span>
            ))}
          </pre>
        </div>
      )}

      {!shouldShowInline && (
        <p className="text-xs text-muted-foreground italic">
          Content too long for inline diff. Use the diff viewer below for details.
        </p>
      )}
    </div>
  );
}

export default TextDiffSummary;
